"use client";

import React from "react";
import { ArrowRight, CircleCheck, Loader, Shield } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const INTEREST_OPTIONS = ["Tropical Getaways", "Mountain Expeditions", "Cultural Immersions", "Luxury Cruises", "Adventure Trekking"];
function VisionaryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-md border border-primary/20 bg-secondary/30 p-8 text-center backdrop-blur-md">
        <div className="flex size-16 items-center justify-center rounded-md bg-primary/20 text-primary">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">
          Welcome to the Expedition
        </h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Join Another List
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Full Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Marco Polo" className="flex h-10 w-full rounded-md border border-border bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="explorer@wanderlust.com" className="flex h-10 w-full rounded-md border border-border bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Preferred Region</FormLabel>
              <FormControl>
                <input type="text" placeholder="Southeast Asia" className="flex h-10 w-full rounded-md border border-border bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/60">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Travel Style</FormLabel>
              <FormControl>
                <input type="text" placeholder="Backpacker, Luxury, Solo" className="flex h-10 w-full rounded-md border border-border bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/60">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest_areas" render={({
      field
    }) => <FormItem className="rounded-md border border-border/50 bg-background/30 p-4">
            <FormLabel className="font-default text-base text-foreground">Adventure Preferences</FormLabel>
            <FormDescription className="text-muted-foreground">
              Select the types of expeditions you want to hear about first.
            </FormDescription>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {INTEREST_OPTIONS.map((option, i) => <div data-index={i} key={option} className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <input type="checkbox" className="peer mt-1 size-4 shrink-0 rounded-md border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" checked={field.value?.includes(option)} onChange={e => {
              const current = field.value || [];
              const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
              field.onChange(updated);
            }} />
                  </FormControl>
                  <FormLabel className="font-default font-normal text-sm text-muted-foreground peer-checked:text-foreground cursor-pointer">
                    {option}
                  </FormLabel>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] bg-primary text-primary-foreground hover:opacity-90 font-default uppercase tracking-wider" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Preparing...
          </> : <>
            Subscribe
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
      
      <p className="text-xs text-muted-foreground flex items-center justify-center sm:justify-start gap-1 mt-4 font-mono">
        <Shield className="size-3 text-primary" />
        Secure Log. Read our <Link to="/ContactPage" className="text-primary hover:underline">Privacy Policy</Link>.
      </p>
    </Form>;
}
export default function Newsletter() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224566" id="newsletter" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute top-0 left-0 -translate-x-1/4 -translate-y-1/4 size-[600px] opacity-20 pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687046/img/tropical_leaf_silhouette_left.png" alt="" className="w-full h-full object-contain" />
      </div>
      <div className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 size-[600px] opacity-20 pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687046/img/tropical_leaf_silhouette_right.png" alt="" className="w-full h-full object-contain" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-mono font-medium text-primary">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75"></span>
                <span className="relative inline-flex size-2 rounded-lg bg-primary"></span>
              </span>
              EXPEDITION LOG 001
            </div>
            
            <h2 className="font-default text-4xl font-bold tracking-tighter sm:text-5xl lg:text-7xl text-foreground uppercase">
              THE <span className="text-primary">DISPATCH.</span>
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed font-default">
              Join an elite network of global explorers. Get curated itineraries, hidden gem reviews, and professional travel guides delivered straight to your inbox.
            </p>

            <ul className="space-y-4">
              {["Early access to seasonal vacation packages", "Exclusive tropical destination scouting reports", "Actionable travel hacks & gear reviews"].map((item, i) => <li data-index={i} key={i} className="flex items-center gap-3 text-muted-foreground font-default">
                  <CircleCheck className="size-5 text-primary shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-md border border-border/50 bg-card/40 p-6 sm:p-8 md:p-10 shadow-4xl backdrop-blur-md relative overflow-hidden">
              {}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl rounded-lg -translate-y-1/2 translate-x-1/2" />
              
              <div className="mb-8">
                <h3 className="font-default text-2xl font-semibold text-foreground uppercase tracking-tight">
                  Join the Expeditions
                </h3>
                <p className="text-sm text-muted-foreground mt-2 font-default">
                  Configure your travel preferences for tailored adventure insights.
                </p>
              </div>

              <FormProvider formKey="newsletter_form_stories" formId="newsletter_form_stories" sectionName="Newsletter Signup" defaultValues={{
              full_name: "",
              email_address: "",
              company: "",
              role: "",
              interest_areas: []
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              }
            }} submitText="Subscribe" successMessage="Your compass is set. Check your inbox for your first expedition report.">
                <VisionaryForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}